/**
 * ForgotPasswordPage.tsx — MER password-reset request. Phase-2 P2-D3.
 *
 * The confirmation shown here is deliberately identical whether or not the
 * address has an account. The API returns the same 202 for an unknown address,
 * a known one, an inactive one and a throttled one, and this page must not
 * reintroduce the account-existence oracle the API refuses to be.
 */
import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { csrfHeader } from '../../auth/AuthContext';

const SHELL: React.CSSProperties = {
  background: '#080F1C',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: "'Courier New', Courier, monospace",
  backgroundImage: `linear-gradient(rgba(26,48,80,0.15) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(26,48,80,0.15) 1px, transparent 1px)`,
  backgroundSize: '40px 40px',
};

const LABEL: React.CSSProperties = { fontSize: 10, letterSpacing: 2, color: '#3A5070', display: 'block', marginBottom: 6 };
const INPUT: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 13,
  background: '#0A1628', border: '1px solid #1A3050',
  borderRadius: 3, color: '#E8EDF5', fontFamily: 'inherit', outline: 'none',
};

export function ForgotPasswordPage() {
  const [email, setEmail]       = useState('');
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  const [available, setAvailable] = useState(true);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await fetch('/api/auth/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      if (r.status === 404) { setAvailable(false); return; }
      if (r.status === 400) { setError('Please enter a valid email address.'); return; }
      // Every other outcome — including an internal fault — is the same
      // confirmation, by design.
      setSent(true);
    } catch {
      setError('Network error — check your connection.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mer-full-page" style={SHELL}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 10, letterSpacing: 4, color: '#C9A84C', fontWeight: 700 }}>MER ALGO FOOTPRINTS</div>
          <div style={{ fontSize: 12, letterSpacing: 2, color: '#3A5070', marginTop: 10 }}>RESET PASSWORD</div>
        </div>

        {!available && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            Password reset is not currently available. Please contact an administrator.
          </div>
        )}

        {available && sent && (
          <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7 }}>
            If that address has an account, a reset link has been sent. The link
            expires shortly and can be used once.
          </div>
        )}

        {available && !sent && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="fp-email" style={LABEL}>EMAIL</label>
              <input
                id="fp-email" type="email" value={email} required autoComplete="email"
                onChange={e => setEmail(e.target.value)} style={INPUT}
              />
            </div>
            {error && <div style={{ fontSize: 11, color: '#C0554E', marginBottom: 12 }}>{error}</div>}
            <button
              type="submit" disabled={loading}
              style={{
                width: '100%', padding: '12px', fontSize: 11, fontWeight: 700, letterSpacing: 2,
                background: loading ? '#6A5428' : '#C9A84C', color: '#080F1C',
                border: 'none', borderRadius: 3, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {loading ? 'SENDING…' : 'SEND RESET LINK'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: '#3A5070' }}>
          <Link to="/login" style={{ color: '#C9A84C', textDecoration: 'none' }}>← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
